import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Video, Gamepad2, Sparkles, ArrowRight, CheckCircle2, Heart, Users, Globe2 } from "lucide-react";

export default function Home() {
  const { t } = useLang();
  const pillarIcons = [Heart, Users, Globe2];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#F97316]/10 blur-3xl -translate-y-1/3 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-[#0B3D91]/10 blur-3xl translate-y-1/3 -translate-x-1/3" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-20 lg:pb-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 fade-up">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E4EAF7] text-xs font-semibold text-[#0B3D91] tracking-wide" data-testid="hero-badge">
                <span className="w-1.5 h-1.5 rounded-full brand-orange-bg" /> {t.hero.badge}
              </span>
              <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0B3D91]" data-testid="hero-title">
                {t.hero.title}<br />
                <span className="brand-orange">{t.hero.titleAccent}</span>
              </h1>
              <p className="mt-5 text-base sm:text-lg text-[#5A6A94] max-w-2xl leading-relaxed" data-testid="hero-subtitle">
                {t.hero.subtitle}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" className="btn-accent" data-testid="hero-cta-primary">
                  {t.hero.ctaPrimary} <ArrowRight size={16} />
                </Link>
                <Link to="/exercises" className="btn-outline" data-testid="hero-cta-secondary">
                  {t.hero.ctaSecondary}
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#5A6A94]">
                <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#16A34A]" /> IT · EN · PA · HI · UR</div>
                <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#16A34A]" /> Live Google Meet</div>
                <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#16A34A]" /> A0 → B2 illimitati</div>
              </div>
            </div>
            <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
              <div className="relative">
                <div className="absolute -inset-4 bg-[#F97316]/15 rounded-3xl -rotate-3" />
                <div className="relative rounded-3xl overflow-hidden bg-[#FFF7E6] shadow-xl">
                  <img
                    src="/chikoo_mascot.png"
                    alt="Chikoo — your AI guide"
                    className="w-full aspect-[4/3] object-cover floating"
                    data-testid="hero-mascot"
                  />
                </div>
                <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-[#E4EAF7]">
                  <div className="w-10 h-10 rounded-full brand-blue-bg text-white flex items-center justify-center font-bold text-sm">€30</div>
                  <div>
                    <div className="text-xs text-[#8494B8]">Quota unica</div>
                    <div className="text-sm font-semibold text-[#0B3D91]">Corso completo</div>
                  </div>
                </div>
                <div className="absolute -top-3 -right-3 bg-[#0B3D91] text-white rounded-full px-3 py-1 text-xs font-bold shadow-lg">
                  Ciao! I'm Chikoo 👋
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT US */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" id="about" data-testid="about-section">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold uppercase tracking-widest text-[#F97316]">{t.about.title}</span>
            <h2 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl text-[#0B3D91] leading-tight">
              {t.tagline}
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base sm:text-lg text-[#3D4A6E] leading-relaxed" data-testid="about-body">
              {t.about.body}
            </p>
          </div>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {t.about.pillars.map((p, i) => {
            const Icon = pillarIcons[i];
            return (
              <div key={p.t} className="card-elevated p-6 fade-up" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`pillar-${i}`}>
                <div className={`w-11 h-11 rounded-xl ${i === 1 ? "brand-orange-bg" : "brand-blue-bg"} text-white flex items-center justify-center mb-3`}>
                  <Icon size={20} />
                </div>
                <h3 className="font-display font-bold text-lg text-[#0B3D91]">{p.t}</h3>
                <p className="mt-1.5 text-sm text-[#5A6A94] leading-relaxed">{p.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {[
            { icon: Video, title: t.features.f1Title, desc: t.features.f1Desc, tint: "brand-blue-bg" },
            { icon: Gamepad2, title: t.features.f2Title, desc: t.features.f2Desc, tint: "brand-orange-bg" },
            { icon: Sparkles, title: t.features.f3Title, desc: t.features.f3Desc, tint: "bg-[#16A34A]" },
          ].map((f, i) => (
            <div key={f.title} className="card-elevated p-7 fade-up" style={{ animationDelay: `${i * 0.1}s` }} data-testid={`feature-card-${i}`}>
              <div className={`w-12 h-12 rounded-xl ${f.tint} text-white flex items-center justify-center mb-4`}>
                <f.icon size={22} />
              </div>
              <h3 className="font-display font-bold text-xl text-[#0B3D91]">{f.title}</h3>
              <p className="mt-2 text-sm text-[#5A6A94] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
